import { format } from 'date-fns';
import {
    Channel,
    ChannelGroup,
    ChannelGroupsResponse,
    ChannelItem,
    ChannelsResponse,
    CoverAsset,
    EpisodeCard,
    MovieCard,
    Listing,
    Recording,
    MovieDetails,
    Person,
    Series,
    PersonRole,
    NowAndNext,
    ContinueWatching,
    ContinueWatchingItems,
    ContinueWatchingVOD,
    ContinueWatchingListing,
    SeriesDetails,
    TVSeriesDetailEpisode,
    ChannelPrograms,
    NowAndNextProgram,
    NowAndNextApiResponse,
    NowAndNextData,
    Credit,
    MovieApiResponse,
    VodTerm,
    SeriesApiResponse,
    SeriesEpisodeApiResponse,
    SeriesEpisodesApiResponse,
    VODPurchasesResponse,
} from '../types/Asset';
import { SearchData, SearchDisplayItem } from '../types/Search';
import translate from './fnTranslate';
import { getSubscribeOffer, getVodAssetEntitlementState } from './fnEntitlement';
import { AssetTypes, AssetTypesGS } from '../types/Common';
import { VODAssetEntitlementState, EntitlementState } from '../types/Entitlement';
import { PageData, PageResponse, PageSection, PageSectionCustomAttributes } from '../types/Page';
import { CollectionParams } from '../types/Collection';
import { MainMenuItem, Navigation, NavigationResponse } from '../types/Navigation';
import { EditorialItem } from '../types/Config';
import { EpgChannel } from '../types/EpgTypes';
import { BookmarkItem, BookmarkSectionType, BookmarksResponse } from '../types/Bookmarks';
import { ChannelCover } from '../types/Cover';
import { getLivePlayerURL } from './fnUrl';
import {
    CardItemInput,
    CollectionApiResponse,
    EditorialApiResponse,
    EditorialApiResultItem,
    EpisodeCardItemInput,
    ListingCardItemInput,
    RecordingItemInput,
    SeriesCardItemInput,
} from '../types/ParserInput';
import { ParsedWatchlistItem, ResultItem } from '../types/WatchlistTypes';
import { buildEpisodeTitle } from './fnData';
import { getLocalStorage } from './fnStorage';
import { getRecordingIdFromListingId } from './recordingUtils';
import { getLocalizedField } from './fnLocalizedField';

export const PROG_TYPE = 'progtypeCS';
export const TIME_FORMAT_HH_MM = 'HH:mm';

export type Collection<T> = {
    aspectRatio: string;
    items: Array<T>;
    totalItems: number;
    scheduleReload?: number;
    moreResources?: boolean;
    count?: number;
    pages?: number;
    results?: T[];
    vodAssetState?: VODAssetEntitlementState;
    entitlementState?: EntitlementState;
    title?: string;
    moduleType?: string;
    variety?: string;
    metaTitle?: string;
    metaDescription?: string;
    order?: string;
    orderOptions?: string[];
};

// --- Navigation & Page parsers ---

export const navigationParser = (data: NavigationResponse, lang = 'et'): Navigation => {
    const navigationItemParser = (item: NavigationResponse['navigation_items'][number]): MainMenuItem => ({
        id: item.id,
        pageId: item.page_id,
        page_id: item.page_id,
        title: item.title,
        icon: item.icon_svg,
        type: item.type,
        internalView: item.internal_view,
        customAttributesId: item.custom_attributes?.id ?? null,
        metaTitle: getLocalizedField(item.custom_attributes ?? {}, 'metatitle', lang),
        metaDescription: getLocalizedField(item.custom_attributes ?? {}, 'metadescription', lang),
    });

    return {
        homePageId: data.home_page_id,
        menuItems: data.navigation_items?.map(navigationItemParser) ?? [],
    };
};

const isFlagEnabled = (value: boolean | string | null | undefined): boolean => value === true || String(value).toLowerCase() === 'true';

export const pageParser = (data: PageResponse, lang = 'et'): PageData => {
    const customAttributesParser = (item: PageResponse['page_sections'][number]['custom_attributes']): PageSectionCustomAttributes => ({
        variant: item.variant,
        variety: item.variety as PageSectionCustomAttributes['variety'],
        moduleType: item.module_type?.toUpperCase() as PageSectionCustomAttributes['moduleType'],
        itemPerPage: item.items_per_page,
        type: item.type,
        pagePath: item.path ?? undefined,
        menuTabId: item.menu_tab_id ?? undefined,
        hideTitle: isFlagEnabled(item.hide_title),
    });

    const pageSectionParser = (item: PageResponse['page_sections'][number]): PageSection => ({
        id: item.id,
        title: item.title,
        collectionId: item.collection_id,
        imageAspectRatio: item.image_aspect_ratio,
        expandable: item.expandable,
        expandPageId: item.expand_page_id,
        channelGroupId: item.channel_group_id,
        customAttributes: customAttributesParser(item.custom_attributes),
        type: item.type,
    });

    return {
        id: data.id,
        title: data.title,
        metaTitle: getLocalizedField(data.custom_attributes ?? {}, 'metatitle', lang),
        metaDescription: getLocalizedField(data.custom_attributes ?? {}, 'metadescription', lang),
        pageSections: data.page_sections?.map(pageSectionParser) ?? [],
    };
};

const HERO_SECTION_TYPE = 'HERO';
const BANNER_MODULE_TYPE = 'banner';

export const getCollectionImageSize = (section?: Pick<PageSection, 'type' | 'customAttributes'>): CollectionParams['image_size'] => {
    const isHero = section?.type === HERO_SECTION_TYPE;
    const isBanner = section?.customAttributes?.moduleType?.toLowerCase() === BANNER_MODULE_TYPE;
    return isHero || isBanner ? 'large' : 'small';
};

// --- Channel parsers ---

const channelCardItemParser = (data: ChannelItem): Channel => {
    if (!data) return {} as Channel;

    return {
        id: data.id,
        logo: data.logo_url_dark,
        type: AssetTypes.channel,
        number: data.number,
        name: data.name,
        subscription: !data.additional_services_available,
        additional_services_available: data.additional_services_available,
        favorite: false,
        isHd: ['UHD', 'FHD'].includes(data.max_quality),
        replay: data.replay,
        replay_depth: data.replay_depth,
        live: data.live,
        instant_restart: data.instant_restart,
        timeshift: data.timeshift,
        timeshift_depth: data.timeshift_depth,
        drm_required: data.drm_required,
        supported_stream_formats: data.supported_stream_formats,
        recording: data.recording,
        recording_depth: data.recording_depth,
    };
};

export const channelsParser = (data: ChannelsResponse): Collection<Channel> => {
    const resp = {
        items: [] as Channel[],
        totalItems: 0,
        scheduleReload: -1,
        aspectRatio: null,
    };

    resp.totalItems = data?.count;
    resp.items = data?.results?.map((item) => channelCardItemParser(item));

    return resp;
};

export const parseChannels = (input: Channel[]): EpgChannel[] => {
    if (input == null) return [];
    return input as EpgChannel[];
};

export const channelGroupsParser = (data: ChannelGroupsResponse): ChannelGroup[] => data?.channel_groups ?? [];

export const channelToCover = (channel: Channel, onSubscribeClick?: (channel: Channel) => void): ChannelCover => {
    const needsSubscription = channel.subscription === false;
    return {
        uuid: channel.id,
        id: channel.id,
        path: needsSubscription ? '' : getLivePlayerURL(channel.id),
        title: channel.name,
        metaData: null,
        image: channel.logo,
        landscapeImage: null,
        landscape: false,
        circle: false,
        assetType: AssetTypes.channel,
        rawData: channel,
        number: channel.number,
        subscription: channel.subscription,
        favorite: channel.favorite,
        isHd: channel.isHd,
        onClick: needsSubscription && onSubscribeClick ? () => onSubscribeClick(channel) : undefined,
    };
};

// --- Bookmark parsers ---

export const parseBookmarkItems = (data: BookmarksResponse): BookmarkItem[] => {
    const result: BookmarkItem[] = [];

    if (data) {
        Object.entries(data).forEach(([name, bookmarks]) => {
            if (bookmarks) {
                const newBookmarks = {};
                Object.entries(bookmarks).forEach(([id, isBookmarked]) => {
                    if (isBookmarked) {
                        newBookmarks[id] = isBookmarked;
                    }
                });
                result.push({
                    name: name as BookmarkSectionType,
                    bookmarks: newBookmarks,
                });
            }
        });
    }

    return result;
};

// --- Movie & VOD detail parsers ---

export const movieDetailsParser = (data: MovieApiResponse): MovieDetails => {
    const cast: Person[] = data.credits.map((credit: Credit) => ({
        id: credit.person.id,
        title: credit.person.name,
        image: credit.person.image_url,
        type: AssetTypes.person,
        role: credit.role as PersonRole,
        onClickOpenCollection: false,
    }));

    const vodTerms: VodTerm[] = data.vod_terms.map((term: VodTerm) => ({
        id: term.id,
        type: term.type,
        price: term.price,
        currency: term.currency,
        period: term.period,
        rental_period: term.rental_period,
        watch_period: term.watch_period,
        catalog_id: term.catalog_id,
        catalog_name: term.catalog_name,
        start: term.start,
        end: term.end,
    }));

    return {
        id: data.id,
        title: data.title,
        title_original: data.title_original,
        episodeId: data.id,
        episodeTitle: data.title,
        shortDescription: data.description,
        description: data.description,
        tagline: data.tagline,
        credits: data.credits,
        vod_terms: vodTerms,
        vod_session_until: data.vod_session_until,
        additional_services_available: data.additional_services_available,
        episode_title: data.episode_title,
        episode_title_original: data.episode_title_original,
        image: data.image_url,
        background_image_url: data.background_image_url,
        kind: data.kind,
        vod_series_id: data.vod_series_id,
        season_number: data.season_number,
        episode_number: data.episode_number,
        category: data.category,
        genres: data.genres,
        release_year: data.release_year,
        duration: data.duration,
        production_countries: data.production_countries,
        max_quality: data.max_quality,
        audio_languages: data.audio_languages,
        subtitle_languages: data.subtitle_languages,
        vodAssetState: getVodAssetEntitlementState(data),
        type: AssetTypes.movie,
        price: vodTerms.length > 0 ? vodTerms[0].price : 0,
        imdbRating: data.imdb_rating,
        landscapeImage: data.background_image_url || data.image_url,
        hasTrailer: data.has_trailer,
        cast,
    };
};

export const VODPurchasesParser = (vodPurchases: VODPurchasesResponse): MovieCard[] => {
    if (!vodPurchases?.results) {
        return [];
    }

    return vodPurchases.results
        .filter((purchase) => purchase.vod_asset?.kind !== AssetTypes.episode)
        .map((purchase) => {
            const asset = purchase.vod_asset;
            const vodTerm = purchase.vod_term;

            return {
                id: asset.id,
                titleId: asset.id,
                type: AssetTypes.movie,
                category: asset.category,
                title: asset.title,
                image: asset.image_url,
                image_url: asset.image_url,
                landscapeImage: asset.background_image_url || asset.image_url,
                genres: asset.genres,
                metaData: asset.genres?.join(', '),
                duration: asset.duration,
                countries: asset.production_countries,
                releaseYear: asset.release_year,
                imdbRating: asset.imdb_rating,
                isPortrait: true,
                price: vodTerm.price,
                vodAssetState: VODAssetEntitlementState.ENTITLED,
                watchPeriod: vodTerm.watch_period,
                createdAt: purchase.created_at,
            };
        });
};

// --- Card item parsers (individual asset cards for stripes/collections) ---

export const cardItemParser = (data: CardItemInput, type: AssetTypes): MovieCard => {
    const { item } = data;

    return {
        id: item.id,
        titleId: item.id,
        type,
        category: item.category as unknown as AssetTypes,
        title: item.title,
        image: item.image_url,
        image_url: item.image_url,
        landscapeImage: item.background_image_url || item.image_url || data.image_url,
        metaData: item.genres?.join(', ') ?? '',
        countries: item.production_countries ?? [],
        genres: item.genres,
        releaseYear: item.release_year?.toString(),
        duration: item.duration,
        bookmark: 0,
        firstAvailability: 0,
        imdbRating: item.imdb_rating,
        price: 0,
        vodAssetState: VODAssetEntitlementState.SHOULD_SUBSCRIBE,
        subscribeOffer: getSubscribeOffer(data),
        isPortrait: data.aspectRatio === '2:3',
        audio_languages: [],
        subtitle_languages: [],
        vod_terms: item.vod_terms ?? [],
    };
};

const episodeCardItemParser = (data: EpisodeCardItemInput): EpisodeCard => {
    const { item } = data;

    return {
        id: item.id,
        titleId: item.id,
        episodeId: item.id,
        episode_number: item.episode_number,
        season_number: item.season_number,
        type: item.vod_series_id ? AssetTypes.vod_episode : (data.type.toLowerCase() as unknown as AssetTypes),
        kind: item.kind as unknown as AssetTypes,
        title: item.title,
        episode_title: item.episode_title,
        isPortrait: data.aspectRatio !== '16:9',
        description: item.description,
        image: item.image_url,
        image_url: item.image_url,
        landscapeImage: item.background_image_url || item.image_url || data.image_url,
        releaseYear: item.release_year?.toString(),
        release_year: item.release_year?.toString(),
        countries: item.production_countries ?? [],
        production_countries: item.production_countries ?? [],
        vod_series_id: item.vod_series_id,
        genres: item.genres,
        duration: item.duration,
        bookmark: 0,
        imdbRating: item.imdb_rating,
        price: 0,
        firstAvailability: 0,
        vodAssetState: VODAssetEntitlementState.SHOULD_SUBSCRIBE,
        audio_languages: [],
        subtitle_languages: [],
        vod_terms: item.vod_terms ?? [],
        vod_session_until: item.vod_session_until,
    };
};

const seriesCardItemParser = (data: SeriesCardItemInput): Series => {
    const { item } = data;

    return {
        id: item.id,
        type: AssetTypes.series,
        title: item.title,
        image: item.image_url,
        landscapeImage: item.background_image_url || item.image_url || data.image_url,
        customImage: data.image_url,
        imageAspectRatio: data.aspectRatio,
        metaData: item.genres?.join(', ') ?? '',
        genres: item.genres,
        countries: item.production_countries ?? [],
        releaseYear: item.release_year?.toString(),
        firstAvailability: -1,
        price: 0,
        imdbRating: item.imdb_rating,
        isVOD: true,
    };
};

const listingCardItemParser = (data: ListingCardItemInput): Listing => {
    const { item } = data;
    const { program } = item;
    const playbackPositions = getLocalStorage('playbackPositions');
    const bookmark = playbackPositions?.listing_playback_positions?.[item.id] || 0;

    const isRecording = !!getRecordingIdFromListingId(item.id);

    let type: AssetTypes;
    if (isRecording) {
        type = AssetTypes.recording;
    } else if (program.kind === 'Movie') {
        type = AssetTypes.program;
    } else {
        type = AssetTypes.tv_series_episode;
    }

    return {
        id: item.id,
        channelId: item.channel_id,
        kind: program.kind,
        start: item.start,
        duration: item.duration,
        program_id: program.id,
        title: program.title,
        episodeTitle: program.episode_title,
        tagline: program.tagline,
        image_url: program.image_url,
        type,
        programSeriesId: program.program_series_id,
        seasonNumber: program.season_number,
        episodeNumber: program.episode_number,
        category: program.category,
        genres: program.genres,
        releaseYear: program.release_year?.toString(),
        productionCountries: program.production_countries,
        bookmark,
        aspectRatio: data.aspectRatio,
        isPortrait: data.aspectRatio === '2:3',
    };
};

export const recordingItemParser = (data: RecordingItemInput): Recording => {
    const { listing } = data;
    const { program } = listing;
    const playbackPositions = getLocalStorage('playbackPositions');
    const bookmark = playbackPositions?.listing_playback_positions?.[listing.id] || 0;

    return {
        id: listing.id,
        recordingId: data.id,
        title: program.title,
        bookmark,
        duration: listing.duration,
        releaseYear: program.release_year?.toString(),
        genres: program.genres,
        image: program.image_url,
        image_url: program.image_url,
        landscapeImage: program.image_url,
        type: AssetTypes.recording,
        metaData: program.category,
        programSeriesId: program.program_series_id,
        start: listing.start,
        channelId: listing.channel_id,
    };
};

// --- Collection parsers (resolve mixed-type collections into typed items) ---

export const mixedStripeItemParser = (item: any): CoverAsset => {
    switch (item?.type) {
        case AssetTypesGS.VOD_ASSET:
            if (item?.item?.kind === AssetTypes.episode) {
                return episodeCardItemParser(item);
            }
            return cardItemParser(item, AssetTypes.movie);
        case AssetTypesGS.VOD_SERIES:
            return seriesCardItemParser(item);
        case AssetTypesGS.PROGRAM:
            if (item?.item?.kind === 'Movie') return cardItemParser(item, AssetTypes.program);
            if (item?.item?.kind === 'Episode') return episodeCardItemParser(item);
            console.warn(`Collection Parser: not supported item kind "${item?.item?.kind}" for item type PROGRAM:`, item);
            return null;
        case AssetTypesGS.PROGRAM_SERIES:
            return cardItemParser(item, AssetTypes.tv_series_episode);
        case AssetTypesGS.LISTING:
            return listingCardItemParser(item);
        default:
            console.warn(`Collection Parser: not supported item type "${item?.type}":`, item);
            return null;
    }
};

export const genericCollectionParser = <T>(data: CollectionApiResponse, lang = 'en'): Collection<T> => {
    const items = data.results.map((item) =>
        mixedStripeItemParser({ ...item, title: data.title, aspectRatio: data.image_aspect_ratio })
    ) as T[];

    return {
        items,
        totalItems: data.count,
        pages: data.pages,
        aspectRatio: data.image_aspect_ratio,
        title: data.title,
        scheduleReload: Infinity,
        metaTitle: getLocalizedField(data.custom_attributes, 'metatitle', lang),
        metaDescription: getLocalizedField(data.custom_attributes, 'metadescription', lang),
        order: data.order,
        orderOptions: data.order_options ?? [],
    };
};

// --- Editorial parsers ---

export const editorialItemParser = (item: EditorialApiResultItem): EditorialItem => ({
    title: item?.title,
    style: {
        background_image: item?.image_url,
    },
    action: {
        type: 'collection',
        collection_id: item?.item?.id,
        page_path: item?.item?.custom_attributes?.page_path,
        image_aspect_ratio: item?.item?.image_aspect_ratio,
    },
});

export const editorialParser = (data: EditorialApiResponse): Collection<EditorialItem> => ({
    items: data.results.map(editorialItemParser),
    totalItems: data.count,
    scheduleReload: Infinity,
    aspectRatio: null,
});

// --- Now & Next / EPG parsers ---

const buildSortedChannelQueues = (channelPrograms: ChannelPrograms): ChannelPrograms =>
    Object.fromEntries(
        Object.entries(channelPrograms).map(([channelId, programs]) => [channelId, [...programs].sort((a, b) => a.start - b.start)])
    );

const findNextPrograms = (
    queue: NowAndNextProgram[],
    now: number
): { next: NowAndNextProgram; upcoming: NowAndNextProgram | undefined } | null => {
    const nextIndex = queue.findIndex((prog) => prog.start * 1000 > now);
    if (nextIndex === -1) return null;
    return { next: queue[nextIndex], upcoming: queue[nextIndex + 1] };
};

const updateProgramFromSchedule = (item: NowAndNext, next: NowAndNextProgram, upcoming: NowAndNextProgram | undefined): NowAndNext => ({
    ...item,
    startTime: next.start,
    endTime: next.start + next.duration,
    duration: next.duration,
    title: next.program.title,
    episodeId: next.program.id,
    seriesId: next.program.program_series_id,
    nextBroadcastTitle: upcoming?.program.title ?? '',
    progress: 0,
    bookmark: 0,
});

export const checkAndUpdatePrograms = (storedData: NowAndNextData): { updatedItems: NowAndNext[]; needsUpdate: boolean } => {
    if (!storedData?.currentPrograms?.length) {
        return { updatedItems: [], needsUpdate: false };
    }

    const now = Date.now();
    const sortedQueues = buildSortedChannelQueues(storedData.channelPrograms);
    let needsUpdate = false;

    const updatedItems = storedData.currentPrograms.map((item) => {
        if (now < item.endTime * 1000) return item;

        const result = findNextPrograms(sortedQueues[item.channelId] ?? [], now);
        if (!result) return item;

        needsUpdate = true;
        return updateProgramFromSchedule(item, result.next, result.upcoming);
    });

    return { updatedItems, needsUpdate };
};

const formatProgramTitle = (prog: NowAndNextProgram): string =>
    `${format(new Date(prog.start * 1000), TIME_FORMAT_HH_MM)} ${prog.program.title}`;

const groupByChannel = (programs: NowAndNextProgram[]): ChannelPrograms => {
    const grouped: ChannelPrograms = {};
    programs.forEach((prog) => {
        (grouped[prog.channel_id] ??= []).push(prog);
    });
    Object.values(grouped).forEach((progs) => progs.sort((a, b) => a.start - b.start));
    return grouped;
};

const findCurrentProgram = (programs: NowAndNextProgram[], now: number): NowAndNextProgram | undefined =>
    programs.find((prog) => {
        const startMs = prog.start * 1000;
        const endMs = (prog.start + prog.duration) * 1000;
        return startMs <= now && now < endMs;
    });

export const buildNowAndNextItem = (
    current: NowAndNextProgram,
    next: NowAndNextProgram | undefined,
    channel: Channel | undefined,
    nextRequestIn: number
): NowAndNext => ({
    id: current.id,
    channelId: current.channel_id,
    channelLogo: channel?.logo,
    channelName: current.program.title,
    channelNumber: channel?.number,
    isChannelSubscribed: channel?.subscription ?? false,
    image: current.program.image_url,
    startTime: current.start,
    endTime: current.start + current.duration,
    type: AssetTypes.now_next,
    isLive: null,
    title: formatProgramTitle(current),
    seriesId: current.program.program_series_id,
    episodeId: current.program.id,
    program: current.program,
    duration: current.duration,
    nextBroadcastTitle: next ? formatProgramTitle(next) : '',
    progress: 0,
    bookmark: 0,
    next_request_in: nextRequestIn,
    playOptions: null,
    items: [],
});

const nowAndNextSortNumber = (item: NowAndNext): number =>
    Number.isFinite(item.channelNumber) ? item.channelNumber : Number.MAX_SAFE_INTEGER;

export const parseNowAndNextResponse = (apiResponse: NowAndNextApiResponse, channels: Channel[]): NowAndNextData => {
    const now = Date.now();
    const channelMap = new Map(channels.map((ch) => [ch.id, ch]));
    const channelPrograms = groupByChannel(apiResponse.results);

    const currentPrograms = Object.entries(channelPrograms)
        .reduce<NowAndNext[]>((acc, [channelId, programs]) => {
            const current = findCurrentProgram(programs, now);
            if (!current) return acc;

            const nextIndex = programs.indexOf(current) + 1;
            const next = programs[nextIndex];
            const channel = channelMap.get(channelId);

            acc.push(buildNowAndNextItem(current, next, channel, apiResponse.next_request_in));
            return acc;
        }, [])
        .sort((a, b) => nowAndNextSortNumber(a) - nowAndNextSortNumber(b) || a.channelId.localeCompare(b.channelId));

    return {
        currentPrograms,
        channelPrograms,
        lastUpdated: Date.now(),
        nextUpdateIn: apiResponse.next_request_in,
    };
};

export const parseEpgPrograms = (item: NowAndNextProgram, channel?: Channel) => {
    const startTime = (item.start ?? 0) * 1000;
    const endTime = startTime + (item.duration ?? 0) * 1000;

    return {
        id: item.id,
        channelId: item.channel_id,
        isChannelReplayCapable: channel?.replay ?? false,
        isChannelSubscribed: channel?.subscription ?? false,
        title: item.program?.title ?? translate('PLAYER_NO_INFO'),
        image: item.program?.image_url ?? null,
        startTime,
        endTime,
        realStartTime: startTime,
        realEndTime: endTime,
        start: item.start,
        duration: item.duration,
        isBreak: false,
        seriesId: item.program?.program_series_id,
        episodeId: item.program?.id,
        description: item.program?.description,
        episodeTitle: item.program?.episode_title || item.program?.episode_title_original,
        seasonNumber: item.program?.season_number,
        episodeNumber: item.program?.episode_number,
        genres: item.program?.genres,
        releaseYear: item.program?.release_year,
        countries: item.program?.production_countries,
        playOptions: {
            backdropImage: item.program?.background_image_url ?? null,
            options: null,
        },
    };
};

// --- Continue watching parser ---

export const continueWatchingCollectionParser = (data: { response: ContinueWatching }): Collection<ContinueWatchingItems> => {
    const isListingItem = (item: ContinueWatchingVOD | ContinueWatchingListing): item is ContinueWatchingListing => 'program' in item;

    const items: ContinueWatchingItems[] = data?.response?.results.map((result) => {
        if (isListingItem(result.item)) {
            const { program, channel_id, id: episode_id, duration } = result.item;
            return {
                id: program.id,
                channel_id,
                episode_id,
                title: program.title || result.title,
                duration,
                episode_title: program.episode_title || '',
                description: program.description || '',
                tagline: program.tagline || '',
                image: program.image_url || '',
                kind: program.kind,
                program_series_id: program.program_series_id,
                vod_series_id: program.vod_series_id,
                season_number: program.season_number,
                episode_number: program.episode_number,
                genres: program.genres || [],
                production_countries: program.production_countries || [],
                originalType: result.type,
                type: AssetTypes.continue_watching,
            };
        }

        return {
            id: result.item.id,
            title: result.item.title || result.title,
            duration: result.item.duration,
            episode_title: result.item.episode_title || '',
            episode_id: result.item.episode_id || '',
            description: result.item.description || '',
            tagline: result.item.tagline || '',
            image: result.item.image_url || '',
            kind: result.item.kind,
            program_series_id: result.item.program_series_id,
            vod_series_id: result.item.vod_series_id,
            season_number: result.item.season_number,
            episode_number: result.item.episode_number,
            genres: result.item.genres || [],
            production_countries: result.item.production_countries || [],
            originalType: result.type,
            type: AssetTypes.continue_watching,
            vod_terms: result.item.vod_terms || [],
        };
    });

    return {
        items,
        entitlementState: null,
        aspectRatio: data.response?.image_aspect_ratio,
        count: data.response?.count,
        pages: data.response?.pages,
        totalItems: data.response?.count,
        scheduleReload: -1,
    };
};

// --- Series detail parsers (series metadata, episodes, credits) ---

const mapCredits = (credits: Credit[]): Person[] =>
    credits?.map((credit) => ({
        id: credit.person.id,
        name: credit.person.name,
        title: credit.person.name,
        role: credit.role as PersonRole,
        image: credit.person.image_url,
        type: AssetTypes.person,
    })) || [];

const parseSeriesDetails = (seriesData: SeriesApiResponse): SeriesDetails<TVSeriesDetailEpisode> => ({
    id: seriesData.id,
    title: seriesData.title,
    type: AssetTypes.series,
    image_url: seriesData.background_image_url || seriesData.image_url,
    landscapeImage: seriesData.background_image_url || seriesData.image_url,
    description: seriesData.description,
    genres: seriesData.genres,
    releaseYear: seriesData.release_year?.toString(),
    productionCountries: seriesData.production_countries,
    credits: seriesData.credits,
    category: seriesData.category,
    episodeNumber: seriesData.episode_number,
    episode_title: seriesData.episode_title,
    seasonNumber: seriesData.season_number,
    imdbRating: seriesData.imdb_rating,
    programSeriesId: seriesData.program_series_id,
    hasTrailer: seriesData.has_trailer,
});

const parseBaseEpisode = (result: SeriesEpisodeApiResponse, playbackPositions: any) => {
    const firstListing = result.listings?.[0];
    const credits = mapCredits(result.credits);
    const releaseYear = result.release_year?.toString();

    return {
        id: result.id,
        episodeId: result.id,
        titleId: result.id,
        title: buildEpisodeTitle(result.episode_title, result.season_number, result.episode_number),
        episodeTitle: result.episode_title,
        episode_title: result.episode_title,
        episodeTitleShort: result.episode_title,
        description: result.description,
        longDescription: result.description,
        tagline: result.tagline || '',
        episode_number: result.episode_number,
        season_number: result.season_number,
        image: result.image_url,
        landscapeImage: result.image_url,
        image_url: result.image_url,
        genres: result.genres,
        credits,
        cast: credits,
        production_countries: result.production_countries,
        countries: result.production_countries,
        releaseYear,
        release_year: releaseYear,
        audio_languages: result.audio_languages || [],
        subtitle_languages: [] as string[],
        bookmark: firstListing?.id ? playbackPositions?.listing_playback_positions?.[firstListing.id] || 0 : 0,
        hasTrailer: result.has_trailer,
        isRecorded: false,
        isRecordingSubscribed: false,
        series_recording_allowed: firstListing?.series_recording_allowed || false,
        duration: result.duration,
        firstAvailability: firstListing?.start || 0,
        isPortrait: false,
        price: 0,
        playOptions: { options: [], backdropImage: result.background_image_url || '' },
        start: firstListing?.start || 0,
        recording_until: firstListing?.recording_until || 0,
        replay_until: firstListing?.replay_until || 0,
        restart_until: firstListing?.restart_until || 0,
        kind: result.kind as AssetTypes,
        vod_terms: result.vod_terms || [],
    };
};

const parseVodEpisode = (result: SeriesEpisodeApiResponse, baseEpisode: ReturnType<typeof parseBaseEpisode>, playbackPositions: any) => ({
    ...baseEpisode,
    type: AssetTypes.vod_episode,
    vod_series_id: result.vod_series_id,
    kind: result.kind as AssetTypes,
    vod_terms: result.vod_terms || [],
    vod_session_until: result.vod_session_until,
    price: result.vod_terms?.[0]?.price,
    bookmark: playbackPositions?.vod_asset_playback_positions?.[result.id] || 0,
    additional_services_available: result.additional_services_available,
});

const parseTvEpisode = (result: SeriesEpisodeApiResponse, baseEpisode: ReturnType<typeof parseBaseEpisode>, seriesId: string) => ({
    ...baseEpisode,
    type: AssetTypes.tv_series_episode,
    program_series_id: result.program_series_id || seriesId,
    listings:
        result.listings?.map((listing) => ({
            id: listing.id,
            channelId: listing.channel_id,
            start: listing.start,
            duration: listing.duration,
            recording_until: listing.recording_until,
            replay_until: listing.replay_until,
            restart_until: listing.restart_until,
            series_recording_allowed: listing.series_recording_allowed,
            additional_services_available: listing.additional_services_available,
        })) || [],
    playOptions: { options: [], backdropImage: '' },
});

export const seriesDetailsParser = (
    seriesData: SeriesApiResponse,
    episodesData: SeriesEpisodesApiResponse,
    isVOD: boolean
): { parsedSeriesDetails: SeriesDetails<TVSeriesDetailEpisode>; parsedEpisodes: TVSeriesDetailEpisode[] } => {
    const parsedSeriesDetails = parseSeriesDetails(seriesData);
    const playbackPositions = getLocalStorage('playbackPositions');

    const parsedEpisodes =
        episodesData?.results?.map((result) => {
            const baseEpisode = parseBaseEpisode(result, playbackPositions);
            return isVOD ? parseVodEpisode(result, baseEpisode, playbackPositions) : parseTvEpisode(result, baseEpisode, seriesData.id);
        }) || [];

    return { parsedSeriesDetails, parsedEpisodes };
};

/**
 * Normalizes a fully-detailed episode response (TV listing from `?event=` or VOD asset from
 * `?episode=`) into the parsed episodes-list shape, so the series header can render a selected episode.
 */
export const detailEpisodeToListEpisode = (detail: any, isVOD: boolean): TVSeriesDetailEpisode | null => {
    if (!detail) return null;

    const playbackPositions = getLocalStorage('playbackPositions');

    if (isVOD) {
        const baseEpisode = parseBaseEpisode(detail as SeriesEpisodeApiResponse, playbackPositions);
        const parsed = parseVodEpisode(detail as SeriesEpisodeApiResponse, baseEpisode, playbackPositions);
        return {
            ...parsed,
            vod_session_until: detail.vod_session_until,
            vodAssetState: getVodAssetEntitlementState(detail),
        } as TVSeriesDetailEpisode;
    }

    const { program } = detail;
    const adapted: SeriesEpisodeApiResponse = {
        id: program?.id ?? detail.id,
        title: program?.title,
        episode_title: program?.episode_title,
        description: program?.description,
        tagline: program?.tagline,
        image_url: program?.image_url,
        background_image_url: program?.background_image_url,
        kind: program?.kind,
        category: program?.category,
        genres: program?.genres,
        release_year: program?.release_year,
        production_countries: program?.production_countries,
        credits: program?.credits,
        imdb_rating: program?.imdb_rating,
        season_number: program?.season_number,
        episode_number: program?.episode_number,
        duration: detail.duration,
        has_trailer: program?.has_trailer,
        audio_languages: detail.audio_languages,
        program_series_id: program?.program_series_id ?? detail.program_series_id,
        listings: [
            {
                id: detail.id,
                channel_id: detail.channel_id,
                start: detail.start,
                duration: detail.duration,
                recording_until: detail.recording_until,
                replay_until: detail.replay_until,
                restart_until: detail.restart_until,
                series_recording_allowed: detail.series_recording_allowed,
                additional_services_available: detail.additional_services_available,
            },
        ],
    };

    const baseEpisode = parseBaseEpisode(adapted, playbackPositions);
    return parseTvEpisode(adapted, baseEpisode, adapted.program_series_id) as TVSeriesDetailEpisode;
};

// --- Search parser ---

export const SearchParser = (searchResponses: SearchData[], collectionId: string): SearchDisplayItem[] => {
    const searchResponse = searchResponses.find((response) => response?.id === collectionId);

    if (!searchResponse?.results || !searchResponse.results.length) {
        return [];
    }

    const TYPE_MAPPING: Record<string, any['type']> = {
        PROGRAMS: 'program',
        PROGRAM_SERIES: 'series',
        VOD_ASSET: 'movie',
        VOD_SERIES: 'vod_series',
    };

    return searchResponse.results.map((item) => {
        const asset = item?.item;

        return {
            id: asset.id || item.id || '',
            titleId: asset.id || item.id || '',
            type: TYPE_MAPPING[item.type] || 'program',
            channelId: asset.channel_id || item.channel_id || '',
            category: asset.category || '',
            title: item.title || asset.title || '',
            description: asset.description || '',
            image: item.image_url || asset.image_url || '',
            image_url: item.image_url || asset.image_url || '',
            landscapeImage: asset.background_image_url || asset.image_url || '',
            genres: asset.genres || [],
            metaData: asset.genres?.join(', ') || '',
            duration: asset.duration,
            countries: asset.production_countries,
            releaseYear: asset.release_year,
            imdbRating: asset.imdb_rating,
            isPortrait: true,
            resolution: asset.max_quality || 'HD',
            vod_terms: asset.vod_terms,
            isSearch: true,
        };
    });
};

// --- Watchlist parser ---

export const WatchlistParser = (results: ResultItem[], image_aspect_ratio: string): ParsedWatchlistItem[] => {
    if (!results?.length || !Array.isArray(results)) {
        return [];
    }

    return results
        .map((result) => {
            const { item } = result;
            const isPortrait = image_aspect_ratio === '2:3';

            const parsed: ParsedWatchlistItem = {
                id: item.id,
                title: item.title,
                image: item.image_url || result.image_url || '',
                background_image_url: item.background_image_url || '',
                description: item.description || '',
                tagline: item.tagline || '',
                metaData: item.genres?.join(', ') || '',
                genres: item.genres || [],
                category: item.category || '',
                duration: item.duration,
                isPortrait,
                kind: item.kind,
                channelId: item.channel_id || '',
                type: result.type.toLowerCase(),
                release_year: item.release_year,
                imdb_rating: item.imdb_rating,
                watchlistItem: true,
            };

            return parsed;
        })
        .filter(Boolean);
};
